import { useCallback, useEffect, useState } from "react";
import { UserRound } from "lucide-react";
import { toast } from "react-toastify";
import { useTranslation } from "../../i18n";
import type { DeskClaim } from "../../types/Dispatch";
import { getOrderClaim, leaderReleaseOrder } from "./deskSessionApi";
import { claimBeingPacked } from "./deskOrderState";
import ReleaseClaimModal from "./release-claim-modal";

// Admin › Orders modal: "Being packed by {name} at {desk} since {time}" with
// a leader Release. Only an order at dispatch can be claimed, so nothing is
// asked for any other status. No claim, or a 403 / 404 / 503, renders nothing.
const OrderPackClaim = ({ orderId, orderNumber, status, onReleased }: {
    orderId: string;
    orderNumber: string;
    status: string;
    onReleased?: () => void;
}) => {
    const { t } = useTranslation();
    const [claim, setClaim] = useState<DeskClaim | null>(null);
    const [confirming, setConfirming] = useState(false);
    const [busy, setBusy] = useState(false);

    const load = useCallback(async () => {
        const res = await getOrderClaim(orderId);
        setClaim(res.ok ? (res.data.claim ?? null) : null);
    }, [orderId]);

    useEffect(() => {
        setClaim(null);
        if (status === "dispatch") void load();
    }, [orderId, status, load]);

    const release = async () => {
        if (busy) return;
        setBusy(true);
        const res = await leaderReleaseOrder(orderId);
        setBusy(false);
        if (res.ok || (!res.ok && res.kind === "rejected" && res.status === 404)) {
            toast.success(t("packing_now_released"));
            setClaim(null);
            onReleased?.();
        } else {
            toast.error(t("packing_now_release_failed"));
            await load();
        }
        setConfirming(false);
    };

    if (!claim) return null;
    return (
        <>
            <div className="dispatch-detail-hold-banner" role="status">
                <UserRound size={14} />
                <span>{claimBeingPacked(t, claim)}</span>
                <button
                    type="button"
                    className="btn btn-secondary dispatch-modal-btn"
                    style={{ marginLeft: "auto" }}
                    onClick={() => setConfirming(true)}
                >
                    {t("packing_now_release")}
                </button>
            </div>
            {confirming && (
                <ReleaseClaimModal
                    orderNumber={orderNumber}
                    packerName={claim.name ?? t("desk_claim_unknown_packer")}
                    busy={busy}
                    onConfirm={release}
                    onCancel={() => setConfirming(false)}
                />
            )}
        </>
    );
};

export default OrderPackClaim;
